'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import RevealHero from './RevealHero';
import VoteBars from './VoteBars';
import ParticipantGrid from './ParticipantGrid';
import CountdownReveal from './CountdownReveal';
import { createClient } from '@/lib/supabase/client';
import { Users, Share2, RotateCcw, Eye, ArrowLeft, Baby } from 'lucide-react';

interface Vote {
  id: string;
  participantName: string;
  choice: 'boy' | 'girl';
  timestamp: string;
}

interface EventConfig {
  parentName1: string;
  parentName2: string;
  babyNameHint: string;
  dueDate: string;
  customMessage: string;
  colorTheme: string;
  eventTitle: string;
}

const defaultEvent: EventConfig = {
  parentName1: '',
  parentName2: '',
  babyNameHint: '',
  dueDate: '',
  customMessage: '¡Gracias a todos por participar!',
  colorTheme: 'classic',
  eventTitle: 'Revelación de Género',
};

export default function ResultsRevealClient() {
  const [event, setEvent] = useState<EventConfig>(defaultEvent);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedSex, setSelectedSex] = useState<'boy' | 'girl' | null>(null);
  const [savingSex, setSavingSex] = useState(false);
  const hasLaunched = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const supabase = createClient();

      // Get token from localStorage (organizer's device)
      const storedToken = localStorage.getItem('babyreveal_organizer_token');

      if (storedToken) {
        const { data: eventData } = await supabase
          .from('events')
          .select('*')
          .eq('token', storedToken)
          .maybeSingle();

        if (eventData) {
          setEvent({
            parentName1: eventData.parent_name1 || '',
            parentName2: eventData.parent_name2 || '',
            babyNameHint: eventData.baby_name_hint || '',
            dueDate: eventData.due_date || '',
            customMessage: eventData.custom_message || '',
            colorTheme: eventData.color_theme || 'classic',
            eventTitle: eventData.event_title || 'Revelación de Género',
          });

          // Restore revealed state
          if (eventData.revealed) {
            setRevealed(true);
          }

          // Restore selected sex
          if (eventData.baby_sex) {
            setSelectedSex(eventData.baby_sex as 'boy' | 'girl');
          }
        }

        const { data: votesData } = await supabase
          .from('votes')
          .select('id, participant_name, choice, timestamp')
          .eq('event_token', storedToken)
          .order('timestamp', { ascending: false });

        if (votesData) {
          setVotes(
            votesData.map((v) => ({
              id: v.id,
              participantName: v.participant_name,
              choice: v.choice as 'boy' | 'girl',
              timestamp: v.timestamp,
            }))
          );
        }

        if (typeof window !== 'undefined') {
          setShareUrl(`${window.location.origin}/participation-screen?t=${storedToken}`);
        }
      } else {
        if (typeof window !== 'undefined') {
          setShareUrl(`${window.location.origin}/participation-screen`);
        }
      }

      setLoading(false);
    };

    loadData();
  }, []);

  const boyVotes = votes.filter((v) => v.choice === 'boy').length;
  const girlVotes = votes.filter((v) => v.choice === 'girl').length;
  const totalVotes = votes.length;
  const boyPct = totalVotes > 0 ? Math.round((boyVotes / totalVotes) * 100) : 50;
  const girlPct = totalVotes > 0 ? Math.round((girlVotes / totalVotes) * 100) : 50;

  // Winner: use organizer's selected sex if set, otherwise calculate from votes
  const voteWinner: 'boy' | 'girl' | 'tie' =
    boyVotes > girlVotes ? 'boy' : girlVotes > boyVotes ? 'girl' : 'tie';
  const winner: 'boy' | 'girl' | 'tie' = selectedSex ?? voteWinner;

  const handleSexSelect = async (sex: 'boy' | 'girl') => {
    setSelectedSex(sex);
    setSavingSex(true);
    const storedToken = localStorage.getItem('babyreveal_organizer_token');
    if (storedToken) {
      const supabase = createClient();
      await supabase
        .from('events')
        .update({ baby_sex: sex })
        .eq('token', storedToken);
    }
    setSavingSex(false);
  };

  const handleReveal = () => {
    setShowCountdown(true);
  };

  const handleCountdownComplete = async () => {
    setShowCountdown(false);
    setRevealed(true);

    // Persist revealed state to Supabase
    const storedToken = localStorage.getItem('babyreveal_organizer_token');
    if (storedToken) {
      const supabase = createClient();
      await supabase
        .from('events')
        .update({ revealed: true })
        .eq('token', storedToken);
    }

    if (hasLaunched.current) return;
    hasLaunched.current = true;

    import('canvas-confetti').then((mod) => {
      const confetti = mod.default;
      const colors =
        winner === 'boy'
          ? ['#60a5fa', '#3b82f6', '#93c5fd', '#ffffff', '#dbeafe']
          : winner === 'girl'
          ? ['#f472b6', '#ec4899', '#f9a8d4', '#ffffff', '#fce7f3']
          : ['#60a5fa', '#f472b6', '#ffffff'];

      const burst = () => {
        confetti({ particleCount: 80, spread: 80, origin: { y: 0.5 }, colors });
        confetti({ particleCount: 50, angle: 60, spread: 60, origin: { x: 0, y: 0.5 }, colors });
        confetti({ particleCount: 50, angle: 120, spread: 60, origin: { x: 1, y: 0.5 }, colors });
      };

      burst();
      setTimeout(burst, 600);
      setTimeout(burst, 1200);
    });
  };

  const handleReset = async () => {
    if (confirm('¿Resetear todos los votos? Esta acción no se puede deshacer.')) {
      const storedToken = localStorage.getItem('babyreveal_organizer_token');
      if (storedToken) {
        const supabase = createClient();
        await supabase.from('votes').delete().eq('event_token', storedToken);
        await supabase.from('events').update({ revealed: false, baby_sex: null }).eq('token', storedToken);
      }
      setVotes([]);
      setRevealed(false);
      setSelectedSex(null);
    }
  };

  const handleWhatsAppShare = () => {
    const parentNames = [event.parentName1, event.parentName2].filter(Boolean).join(' & ');
    const msg = encodeURIComponent(
      `🎉 ¡${event.eventTitle}!\n\n${parentNames ? `${parentNames} revelan: ` : ''}¡Es ${winner === 'boy' ? 'niño 💙' : winner === 'girl' ? 'niña 🩷' : 'empate 💙🩷'}!\n\nVotaron ${totalVotes} personas. ¡Gracias a todos!`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-celebration-gradient flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-celebration-gradient hearts-bg">
      {/* Header */}
      <header className="w-full px-4 py-4 max-w-screen-xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AppLogo size={34} />
          <span className="font-display text-lg font-700 text-foreground">BabyReveal</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm font-600 text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full hover:bg-muted"
          >
            <ArrowLeft size={14} />
            Editar evento
          </Link>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 pb-16">
        {/* Page title */}
        <div className="text-center pt-4 pb-8">
          <h1 className="text-3xl md:text-4xl font-800 text-foreground mb-2">
            {event.eventTitle}
          </h1>
          <p className="text-muted-foreground font-500">
            {(event.parentName1 || event.parentName2)
              ? `${[event.parentName1, event.parentName2].filter(Boolean).join(' & ')} · `
              : ''}{totalVotes} participantes
          </p>
        </div>

        {/* Countdown overlay */}
        {showCountdown && (
          <CountdownReveal onComplete={handleCountdownComplete} />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Main result */}
          <div className="lg:col-span-2 space-y-5">
            {/* Sex selector card — shown before reveal */}
            {!revealed && (
              <div className="bg-card rounded-2xl border border-border card-shadow p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Baby size={15} className="text-muted-foreground" />
                  <h3 className="text-sm font-700 text-foreground">Sexo del bebé</h3>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {selectedSex ? (selectedSex === 'boy' ? '💙 Niño seleccionado' : '🩷 Niña seleccionada') : 'Elige el sexo real del bebé'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                  Selecciona el sexo real del bebé. Esto determinará el resultado que se mostrará al revelar, independientemente de los votos.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleSexSelect('boy')}
                    disabled={savingSex}
                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all active:scale-95 ${
                      selectedSex === 'boy' ?'border-blue-400 bg-blue-50 shadow-md' :'border-border hover:border-blue-200 hover:bg-blue-50/50'
                    }`}
                  >
                    <span className="text-3xl">💙</span>
                    <span className={`text-sm font-700 ${selectedSex === 'boy' ? 'text-blue-600' : 'text-foreground'}`}>
                      Niño
                    </span>
                    {selectedSex === 'boy' && (
                      <span className="text-xs font-600 text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">✓ Seleccionado</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleSexSelect('girl')}
                    disabled={savingSex}
                    className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all active:scale-95 ${
                      selectedSex === 'girl' ?'border-pink-400 bg-pink-50 shadow-md' :'border-border hover:border-pink-200 hover:bg-pink-50/50'
                    }`}
                  >
                    <span className="text-3xl">🩷</span>
                    <span className={`text-sm font-700 ${selectedSex === 'girl' ? 'text-pink-600' : 'text-foreground'}`}>
                      Niña
                    </span>
                    {selectedSex === 'girl' && (
                      <span className="text-xs font-600 text-pink-500 bg-pink-100 px-2 py-0.5 rounded-full">✓ Seleccionada</span>
                    )}
                  </button>
                </div>
                {savingSex && (
                  <p className="text-xs text-muted-foreground text-center mt-3">Guardando...</p>
                )}
              </div>
            )}

            {/* Reveal hero */}
            <RevealHero
              revealed={revealed}
              winner={winner}
              boyVotes={boyVotes}
              girlVotes={girlVotes}
              totalVotes={totalVotes}
              event={event}
              onReveal={handleReveal}
              selectedSex={selectedSex}
            />

            {/* Vote bars */}
            {revealed && (
              <VoteBars
                boyVotes={boyVotes}
                girlVotes={girlVotes}
                totalVotes={totalVotes}
                boyPct={boyPct}
                girlPct={girlPct}
                votes={votes}
              />
            )}
          </div>

          {/* Right: Participants + actions */}
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="bg-card rounded-2xl border border-border card-shadow p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={16} className="text-muted-foreground" />
                <h3 className="text-sm font-700 text-foreground">Resumen de votos</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                  <div className="text-2xl font-800 text-blue-500 font-tabular">{boyVotes}</div>
                  <div className="text-xs font-600 text-blue-400 mt-0.5">💙 Niño</div>
                </div>
                <div className="bg-pink-50 rounded-xl p-3 text-center border border-pink-100">
                  <div className="text-2xl font-800 text-pink-500 font-tabular">{girlVotes}</div>
                  <div className="text-xs font-600 text-pink-400 mt-0.5">🩷 Niña</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border text-center">
                <span className="text-xs text-muted-foreground">Total: </span>
                <span className="text-sm font-700 text-foreground font-tabular">{totalVotes} votos</span>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-card rounded-2xl border border-border card-shadow p-5 space-y-2">
              <h3 className="text-sm font-700 text-foreground mb-3">Acciones</h3>

              <Link
                href={shareUrl || '/participation-screen'}
                className="w-full flex items-center gap-2.5 py-2.5 px-3 rounded-xl border border-border hover:bg-muted text-sm font-600 text-foreground transition-all active:scale-95"
              >
                <Eye size={15} className="text-muted-foreground" />
                Ver pantalla de votación
              </Link>

              {revealed && (
                <button
                  onClick={handleWhatsAppShare}
                  className="w-full flex items-center gap-2.5 py-2.5 px-3 rounded-xl bg-[#25D366] text-white text-sm font-600 hover:opacity-90 transition-all active:scale-95"
                >
                  <Share2 size={15} />
                  Compartir resultado por WhatsApp
                </button>
              )}

              <button
                onClick={handleReset}
                className="w-full flex items-center gap-2.5 py-2.5 px-3 rounded-xl border border-red-200 hover:bg-red-50 text-sm font-600 text-red-500 transition-all active:scale-95"
              >
                <RotateCcw size={15} />
                Resetear votos
              </button>
            </div>

            {/* Participant grid */}
            <ParticipantGrid votes={votes} revealed={revealed} />
          </div>
        </div>
      </main>
    </div>
  );
}